import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { selectBoardState } from "@/features/bughurd/boardSelector";
import { setIsDragging } from "@/features/bughurd/slice";
import { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";

interface AutoScrollContainerProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  sx?: SxProps<Theme>;
  onScroll?: (event: React.UIEvent) => void;
  [key: string]: any;
}
export const AutoScrollContainer: React.FC<AutoScrollContainerProps> = ({
  children,
  className,
  style,
  sx,
  onScroll,
  ...props
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const { isDragging } = useAppSelector(selectBoardState);
  const scrollingRef = useRef<NodeJS.Timeout | null>(null);
  const SCROLL_SPEED = 15;
  const SCROLL_SENSITIVITY = 150;
  const SCROLL_INTERVAL = 10;

  const checkAndScroll = (clientX: number) => {
    if (!containerRef.current || !isDragging) return;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();

    if (scrollingRef.current) {
      clearInterval(scrollingRef.current);
      scrollingRef.current = null;
    }

    const distanceFromLeft = clientX - rect.left;
    const distanceFromRight = rect.right - clientX;

    let scrollAmount = 0;
    let scrollDirection = 0;
    if (distanceFromLeft < SCROLL_SENSITIVITY) {
      const speedFactor = Math.pow(
        1 - distanceFromLeft / SCROLL_SENSITIVITY,
        2
      );
      scrollAmount = SCROLL_SPEED * speedFactor;
      scrollDirection = -1;
    } else if (distanceFromRight < SCROLL_SENSITIVITY) {
      const speedFactor = Math.pow(
        1 - distanceFromRight / SCROLL_SENSITIVITY,
        2
      );
      scrollAmount = SCROLL_SPEED * speedFactor;
      scrollDirection = 1;
    }

    if (scrollAmount > 0) {
      scrollingRef.current = setInterval(() => {
        if (containerRef.current) {
          containerRef.current.scrollLeft += scrollDirection * scrollAmount;
        }
      }, SCROLL_INTERVAL);
    }
  };

  useEffect(() => {
    const handleDragStart = () => {
      dispatch(setIsDragging(true));
    };

    const handleDragEnd = () => {
      dispatch(setIsDragging(false));
      if (scrollingRef.current) {
        clearInterval(scrollingRef.current);
        scrollingRef.current = null;
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        checkAndScroll(e.clientX);
      }
    };

    document.addEventListener("dragstart", handleDragStart);
    document.addEventListener("dragend", handleDragEnd);
    document.addEventListener("mouseup", handleDragEnd);
    document.addEventListener("mousemove", handleMouseMove);

    return () => {
      document.removeEventListener("dragstart", handleDragStart);
      document.removeEventListener("dragend", handleDragEnd);
      document.removeEventListener("mouseup", handleDragEnd);
      document.removeEventListener("mousemove", handleMouseMove);

      if (scrollingRef.current) {
        clearInterval(scrollingRef.current);
      }
    };
  }, [isDragging, dispatch]);

  useEffect(() => {
    const handleBeforeDragStart = () => {
      dispatch(setIsDragging(true));
    };

    window.addEventListener("__rbd-drag-start", handleBeforeDragStart);

    return () => {
      window.removeEventListener("__rbd-drag-start", handleBeforeDragStart);
    };
  }, [dispatch]);

  return (
    <Box
      ref={containerRef}
      className={className}
      style={style}
      sx={{
        overflow: "auto",
        ...sx,
      }}
      onScroll={onScroll}
      {...props}
    >
      {children}
    </Box>
  );
};
